import { useState } from "react";
import {
    IconActivity,
    IconBuilding,
    IconCheck,
    IconChevronRight,
    IconSearch,
    IconShield,
    IconStethoscope,
    IconUser,
} from "../../../components/icons/IconsPaciente";
import PatientState from "./PatientState";
import { mSafe } from "./patientHelpers";
import "./Directorio.css";

const DIR_TABS = [
    { key: "medicos",   label: "Médicos",   icon: <IconUser /> },
    { key: "sedes",     label: "Sedes",     icon: <IconBuilding /> },
    { key: "servicios", label: "Servicios", icon: <IconActivity /> },
    { key: "planes",    label: "Planes",    icon: <IconShield /> },
];

// ─── Modal de confirmación de contratación ──────────────────────────────────

const ConfirmModal = ({ plan, obraSocialNombre, onConfirmar, onCancelar, saving }) => (
    <div className="dir-modal-overlay" onClick={onCancelar}>
        <div className="dir-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dir-modal__icon">
                <IconShield />
            </div>
            <h3 className="dir-modal__title">Contratar plan</h3>
            <p className="dir-modal__body">
                ¿Confirmás la contratación de <strong>{plan.nombre}</strong>
                {obraSocialNombre ? <> de <strong>{obraSocialNombre}</strong></> : ""}?
            </p>
            {plan.coberturas?.length > 0 && (
                <ul className="dir-modal__coberturas">
                    {plan.coberturas.slice(0, 4).map((c, i) => (
                        <li key={i}>
                            <span className="dir-modal__servicio-nombre">
                                {c.servicio?.nombre || "Servicio"}
                            </span>
                            <span className={`dir-modal__nivel nivel--${(c.nivelCobertura || "").toLowerCase()}`}>
                                {c.nivelCobertura === "TOTAL"
                                    ? "100%"
                                    : c.nivelCobertura === "NO_CUBIERTA"
                                        ? "No cubierta"
                                        : c.porcentaje != null
                                            ? `${Math.round(c.porcentaje * 100)}%`
                                            : c.tarifaFija != null
                                                ? `$${c.tarifaFija}`
                                                : "Parcial"}
                            </span>
                        </li>
                    ))}
                    {plan.coberturas.length > 4 && (
                        <li className="dir-modal__more">
                            +{plan.coberturas.length - 4} coberturas más
                        </li>
                    )}
                </ul>
            )}
            <div className="dir-modal__actions">
                <button className="btn-ghost" onClick={onCancelar} disabled={saving}>
                    Cancelar
                </button>
                <button className="primary-button" onClick={onConfirmar} disabled={saving}>
                    {saving ? "Contratando..." : "Confirmar"}
                </button>
            </div>
        </div>
    </div>
);

// ─── Card de plan ───────────────────────────────────────────────────────────

const PlanCard = ({ plan, obraSocialNombre, esActual, onContratar }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className={`dir-plan-card${esActual ? " dir-plan-card--actual" : ""}`}>
            <div className="dir-plan-card__top">
                <div className="dir-plan-card__avatar">
                    <IconShield />
                </div>
                <div className="dir-plan-card__body">
                    <p className="dir-card__name">{plan.nombre}</p>
                    {obraSocialNombre && (
                        <p className="dir-card__sub">{obraSocialNombre}</p>
                    )}
                    <p className="dir-card__meta">
                        {plan.coberturas?.length || 0} cobertura{plan.coberturas?.length !== 1 ? "s" : ""}
                    </p>
                </div>
                <div className="dir-plan-card__actions">
                    {esActual ? (
                        <span className="dir-plan-card__badge-actual">
                            <IconCheck /> Plan actual
                        </span>
                    ) : (
                        <button
                            className="primary-button dir-plan-card__btn-contratar"
                            onClick={() => onContratar(plan)}
                        >
                            Contratar
                        </button>
                    )}
                    {plan.coberturas?.length > 0 && (
                        <button
                            className="btn-ghost dir-plan-card__btn-ver"
                            onClick={() => setExpanded((v) => !v)}
                        >
                            {expanded ? "Ocultar" : "Ver coberturas"}
                        </button>
                    )}
                </div>
            </div>

            {expanded && plan.coberturas?.length > 0 && (
                <div className="dir-plan-card__coberturas">
                    <table className="dir-cob-table">
                        <thead>
                        <tr>
                            <th>Servicio</th>
                            <th>Nivel</th>
                            <th>Cobertura</th>
                        </tr>
                        </thead>
                        <tbody>
                        {plan.coberturas.map((c, i) => (
                            <tr key={i}>
                                <td>{c.servicio?.nombre || "–"}</td>
                                <td>
                                        <span className={`dir-nivel-badge dir-nivel-badge--${(c.nivelCobertura || "").toLowerCase()}`}>
                                            {c.nivelCobertura === "TOTAL"
                                                ? "Total"
                                                : c.nivelCobertura === "NO_CUBIERTA"
                                                    ? "No cubierta"
                                                    : "Parcial"}
                                        </span>
                                </td>
                                <td>
                                    {c.nivelCobertura === "TOTAL"
                                        ? "100%"
                                        : c.nivelCobertura === "NO_CUBIERTA"
                                            ? "–"
                                            : c.porcentaje != null
                                                ? `${Math.round(c.porcentaje * 100)}%`
                                                : c.tarifaFija != null
                                                    ? `$${c.tarifaFija}`
                                                    : "Parcial"}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ─── Directorio principal ───────────────────────────────────────────────────

const Directorio = ({ medicos, sedes, servicios, planes, obrasSociales, paciente, loading, error, onContratarPlan }) => {
    const [tab, setTab] = useState("medicos");
    const [query, setQuery] = useState("");
    const [planAContratar, setPlanAContratar] = useState(null);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");

    const medicosFiltrados = medicos.filter(
        (m) =>
            m.nombre.toLowerCase().includes(query.toLowerCase()) ||
            m.especialidad.toLowerCase().includes(query.toLowerCase()),
    );
    const sedesFiltradas = sedes.filter(
        (s) =>
            mSafe(s.nombre).includes(query.toLowerCase()) ||
            mSafe(s.direccion).includes(query.toLowerCase()),
    );
    const serviciosFiltrados = servicios.filter(
        (s) =>
            s.nombre.toLowerCase().includes(query.toLowerCase()) ||
            s.descripcion.toLowerCase().includes(query.toLowerCase()),
    );
    const planesFiltrados = (planes || []).filter(
        (p) => mSafe(p.nombre).includes(query.toLowerCase()),
    );

    // ID del plan actual del paciente (puede venir como objeto o string)
    const planActualId = String(
        paciente?.plan?._id || paciente?.plan?.id || paciente?.plan || ""
    );

    const getObraSocialDePlan = (planId) => {
        const planIdStr = String(planId);
        return (obrasSociales || []).find((os) =>
            (os.planes || []).some((p) => String(p._id || p.id || p) === planIdStr)
        );
    };

    const handleContratar = async () => {
        if (!planAContratar) return;
        setSaving(true);
        setErrorMsg("");
        try {
            await onContratarPlan(planAContratar.id);
            setSuccessMsg(`Plan "${planAContratar.nombre}" contratado exitosamente.`);
            setPlanAContratar(null);
            setTimeout(() => setSuccessMsg(""), 4000);
        } catch (e) {
            setErrorMsg(e.message || "Error al contratar el plan.");
            setPlanAContratar(null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="patient-section">
            <div className="patient-section__header">
                <div>
                    <p className="eyebrow">Explorar</p>
                    <h2>Directorio</h2>
                </div>
            </div>

            <div className="search-bar">
                <IconSearch />
                <input
                    type="text"
                    placeholder={`Buscar ${tab}...`}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            <div className="dir-tabs">
                {DIR_TABS.map((t) => (
                    <button
                        key={t.key}
                        className={`dir-tab${tab === t.key ? " dir-tab--active" : ""}`}
                        onClick={() => { setTab(t.key); setQuery(""); }}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {successMsg && <p className="dir-success">{successMsg}</p>}
            {errorMsg && <p className="dir-error-msg">{errorMsg}</p>}

            <PatientState error={error} loading={loading} />

            {!loading && tab === "medicos" && (
                <div className="dir-grid">
                    {medicosFiltrados.map((m) => (
                        <div key={m.id} className="dir-card">
                            <div className="dir-card__avatar">{m.nombre.split(" ").pop().charAt(0)}</div>
                            <div className="dir-card__body">
                                <p className="dir-card__name">{m.nombre}</p>
                                <p className="dir-card__sub">{m.especialidad}</p>
                                <p className="dir-card__meta">{m.sedes.map((s) => `Sede ${s}`).join(" - ")}</p>
                            </div>
                            <div className="dir-card__rating">{m.rating !== "-" ? `* ${m.rating}` : ""}</div>
                            <button className="dir-card__cta"><IconChevronRight /></button>
                        </div>
                    ))}
                </div>
            )}

            {!loading && tab === "sedes" && (
                <div className="dir-grid">
                    {sedesFiltradas.map((s) => (
                        <div key={s.id} className="dir-card">
                            <div className="dir-card__avatar dir-card__avatar--building">
                                <IconBuilding />
                            </div>
                            <div className="dir-card__body">
                                <p className="dir-card__name">{s.nombre}</p>
                                <p className="dir-card__sub">{s.direccion}</p>
                                <p className="dir-card__meta">{s.servicios} servicios - {s.telefono}</p>
                            </div>
                            <button className="dir-card__cta"><IconChevronRight /></button>
                        </div>
                    ))}
                </div>
            )}

            {!loading && tab === "servicios" && (
                <div className="dir-grid">
                    {serviciosFiltrados.map((s) => (
                        <div key={s.id} className="dir-card">
                            <div className="dir-card__avatar dir-card__avatar--service">
                                <IconStethoscope />
                            </div>
                            <div className="dir-card__body">
                                <p className="dir-card__name">{s.nombre}</p>
                                <p className="dir-card__sub">{s.descripcion}</p>
                                <p className="dir-card__meta">{s.medicos} medicos disponibles</p>
                            </div>
                            <button className="dir-card__cta"><IconChevronRight /></button>
                        </div>
                    ))}
                </div>
            )}

            {!loading && tab === "planes" && (
                <div className="dir-planes-grid">
                    {planesFiltrados.length === 0 && (
                        <p className="dir-empty">No hay planes disponibles.</p>
                    )}
                    {planesFiltrados.map((plan) => {
                        const os = getObraSocialDePlan(plan.id);
                        return (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                obraSocialNombre={os?.nombre}
                                esActual={planActualId && planActualId === String(plan.id)}
                                onContratar={setPlanAContratar}
                            />
                        );
                    })}
                </div>
            )}

            {planAContratar && (
                <ConfirmModal
                    plan={planAContratar}
                    obraSocialNombre={getObraSocialDePlan(planAContratar.id)?.nombre}
                    onConfirmar={handleContratar}
                    onCancelar={() => setPlanAContratar(null)}
                    saving={saving}
                />
            )}
        </div>
    );
};

export default Directorio;